"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import { Rocket } from "@phosphor-icons/react/dist/ssr";

import { cn } from "@/src/lib/utils";
import { Heading, Text } from "@/src/components/ui/Typography";

import type { GrowthStageData } from "./GrowthStages.types";

interface TrajectoryDiagramProps {
  stages: GrowthStageData[];
}

// Shared coordinate space for the curve, its node markers, and the rocket's
// resting point. Kept as plain constants (not percentages) so the path
// string and the marker positions derive from the same numbers — percentages
// for the HTML overlay are computed from these at render time.
const VIEWBOX_W = 700;
const VIEWBOX_H = 260;
const TICK_BOTTOM_Y = 248; // dashed stems from every node reach down to this line

type Point = { x: number; y: number };

const START: Point = { x: 34, y: 214 };
// The drawn curve stops here — short of the rocket itself — so the line
// never geometrically runs under the icon's bounding box. Phosphor icons
// are vector glyphs with transparent gaps (fins, window, exhaust), so
// z-index alone can't stop the line showing through those gaps if the two
// overlap; leaving a real gap is what actually fixes it.
const LINE_END: Point = { x: 642, y: 45 };
const ROCKET: Point = { x: 652, y: 41 };

// A cubic Bezier's curvature CAN flip direction mid-curve even when its
// height stays monotonic the whole way — it did here (the control polygon
// turned one way and then the other, verified by checking the sign of the
// turn at each vertex), which is exactly what read as a subtle S-bend.
// Defining the curve as an explicit y = f(x) using a plain quadratic ease
// removes that risk outright rather than hoping better-chosen control
// points avoid it: a quadratic's second derivative is a constant (2), so
// it can never change sign — a single accelerating bend is the only shape
// this curve is capable of producing.
const EASE_POWER = 2;

function curveY(x: number): number {
  const progress = (x - START.x) / (LINE_END.x - START.x);
  return START.y - (START.y - LINE_END.y) * progress ** EASE_POWER;
}

const CURVE_SAMPLES = 80;

const PATH_D = (() => {
  const points: Point[] = [];

  for (let i = 0; i <= CURVE_SAMPLES; i++) {
    const x = START.x + ((LINE_END.x - START.x) * i) / CURVE_SAMPLES;
    points.push({ x, y: curveY(x) });
  }

  const [first, ...rest] = points;
  const line = rest.map((p) => `L${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");

  return `M${first.x.toFixed(2)},${first.y.toFixed(2)} ${line}`;
})();

// Marker x-positions sit at the 1/6, 1/2, 5/6 column centers of the 0-700
// viewBox so the dots line up with the 3-column caption grid below; their
// y-positions come directly from the same curve function.
const MARKER_X = [117, 350, 583];
const MARKERS: Point[] = MARKER_X.map((x) => ({ x, y: curveY(x) }));

const DRAW_DURATION = 1.6;
const nodeDelay = (x: number) =>
  ((x - START.x) / (LINE_END.x - START.x)) * DRAW_DURATION + 0.15;
const ROCKET_DELAY = DRAW_DURATION + 0.05;

const pctX = (x: number) => `${(x / VIEWBOX_W) * 100}%`;
const pctY = (y: number) => `${(y / VIEWBOX_H) * 100}%`;

export function TrajectoryDiagram({ stages }: TrajectoryDiagramProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setReducedMotion(true);
      setInView(true);
      return;
    }

    const node = wrapperRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <div ref={wrapperRef} className="relative aspect-700/260 w-full">
        <svg
          viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
          fill="none"
          className="absolute inset-0 z-0 h-full w-full"
          aria-hidden="true"
        >
          <defs>
            <linearGradient
              id="growth-curve-gradient"
              x1={START.x}
              y1={0}
              x2={LINE_END.x}
              y2={0}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="#d2d3d3" />
              <stop offset="100%" stopColor="#202223" />
            </linearGradient>

            {/* Soft blur so the flowing dot reads as a glowing pulse rather
                than a hard-edged circle. */}
            <filter id="flow-glow" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="1.8" />
            </filter>
          </defs>

          <path
            id="growth-curve-path"
            d={PATH_D}
            stroke="url(#growth-curve-gradient)"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength={1}
            style={{
              strokeDasharray: 1,
              strokeDashoffset: inView ? 0 : 1,
              transition: `stroke-dashoffset ${DRAW_DURATION}s cubic-bezier(0.22, 1, 0.36, 1)`,
            }}
          />
          
          {inView && !reducedMotion && (
            <circle r={4} fill="#ffffff" fillOpacity={0.9} filter="url(#flow-glow)">
              <animateMotion
                dur="3s"
                begin={`${ROCKET_DELAY}s`}
                repeatCount="indefinite"
              >
                <mpath href="#growth-curve-path" />
              </animateMotion>
            </circle>
          )}
        </svg>

        {MARKERS.map((marker, index) => {
          const delay = nodeDelay(marker.x);
          const tickHeight = pctY(TICK_BOTTOM_Y - marker.y);

          return (
            <Fragment key={stages[index].n}>
              {/* Static centering + position on the outer element; the
                  scale/opacity reveal lives entirely on this same element
                  since nothing else here needs a transform of its own. */}
              <span
                aria-hidden="true"
                className="trajectory-tick absolute hidden w-px md:block"
                style={{
                  left: pctX(marker.x),
                  top: pctY(marker.y),
                  height: tickHeight,
                  opacity: inView ? 1 : 0,
                  transition: `opacity 0.4s ease-out ${delay}s`,
                }}
              />

              <span
                className="node-pulse absolute hidden h-7 w-7 items-center justify-center rounded-full bg-neutral-700 md:flex"
                style={{
                  left: pctX(marker.x),
                  top: pctY(marker.y),
                  opacity: inView ? 1 : 0,
                  transform: inView
                    ? "translate(-50%, -50%) scale(1)"
                    : "translate(-50%, -50%) scale(0.4)",
                  transition: `opacity 0.35s ease-out ${delay}s, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}s`,
                  ["--pulse-opacity" as string]: 0.35,
                }}
              >
                {/* .node-pulse's ::before ring is itself absolutely
                    positioned, so it paints above plain inline content by
                    default — relative + z-10 gives the number its own
                    stacking order so the ring never washes it out. */}
                <Text
                  as="span"
                  size="labelSM"
                  className="relative z-10 font-semibold text-neutral-50"
                >
                  {stages[index].n}
                </Text>
              </span>
            </Fragment>
          );
        })}

        {/* Ignition flare: purely decorative, positioned by the outer div
            (static translate only) so the keyframe on the inner div can own
            `transform` outright without fighting the centering offset. */}
        <div
          aria-hidden="true"
          className="absolute h-16 w-16 -translate-x-1/2 -translate-y-1/2"
          style={{ left: pctX(ROCKET.x), top: pctY(ROCKET.y) }}
        >
          <div
            className={cn(
              "h-full w-full scale-0 rounded-full bg-[radial-gradient(circle,rgba(66,94,249,0.45),transparent_70%)] opacity-0",
              inView && "rocket-flare",
            )}
            style={
              inView
                ? { ["--flare-delay" as string]: `${ROCKET_DELAY}s` }
                : undefined
            }
          />
        </div>

        {/* Rocket: outer element owns the one-time launch (position +
            scale/opacity entrance); the inner element owns the looping
            idle bob (`chip-float`, reused from AiTooling) so the two
            transforms never collide on the same node. */}
        <div
          className="absolute z-20"
          style={{
            left: pctX(ROCKET.x),
            top: pctY(ROCKET.y),
            opacity: inView ? 1 : 0,
            transform: inView
              ? "translate(-50%, -50%) scale(1)"
              : "translate(-50%, -50%) scale(0.4)",
            transition: `opacity 0.5s ease-out ${ROCKET_DELAY}s, transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${ROCKET_DELAY}s`,
          }}
        >
          <div className={cn(inView && "chip-float")}>
            <Rocket size={34} weight="fill" className="rotate-53 text-neutral-700" />
          </div>
        </div>
      </div>

      {/* Mobile: stacked list, each stage carries its own numbered badge
          since the on-curve nodes are hidden at this breakpoint. */}
      <div className="mt-8 flex flex-col gap-5 md:hidden">
        {stages.map((stage) => (
          <div key={stage.n} className="flex items-start gap-3">
            <span className="flex h-6 w-6 mt-1 flex-none items-center justify-center rounded-full bg-neutral-700">
              <Text as="span" size="labelSM" className="font-semibold text-neutral-50">
                {stage.n}
              </Text>
            </span>

            <div>
              <Heading as="h3" size="headingMD">
                {stage.title}
              </Heading>

              <Text size="bodySM" className="mt-1 text-neutral-500">
                {stage.caption}
              </Text>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: 3-column grid, centered under each on-curve node. */}
      <div className="mt-6 hidden grid-cols-3 gap-4 text-center sm:mt-8 md:grid">
        {stages.map((stage) => (
          <div key={stage.n}>
            <Heading as="h3" size="headingMD">
              {stage.title}
            </Heading>

            <Text size="bodySM" className="mt-2 text-neutral-500">
              {stage.caption}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}
